import { Button, Modal } from '@ai-tutor/ui';
import { CalendarClock, Check, Loader2, PauseCircle } from 'lucide-react';

import { useCancelSubscription } from '@/hooks/account/useBilling';
import { parseUtcDate } from '@/lib/datetime';
import { notify } from '@/lib/notify';

/**
 * Cancellation confirmation. Sets ``cancel_at_period_end=true`` server-
 * side so the user keeps paid access until their current period ends.
 * They can undo via the Resume button on the Current Plan card while
 * the cancel is still scheduled.
 *
 * Retention-first layout: "Keep my subscription" is the primary action and
 * the destructive button is de-emphasized so the red never shouts.
 */
export interface CancelSubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** ISO date of when access ends (for the confirmation copy). */
  periodEnd: string;
}

export function CancelSubscriptionDialog({
  isOpen,
  onClose,
  periodEnd,
}: CancelSubscriptionDialogProps) {
  const cancel = useCancelSubscription();

  const accessEndsOn = parseUtcDate(periodEnd).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handleConfirm = () => {
    cancel.mutate(undefined, {
      onSuccess: () => {
        notify.success('Subscription cancellation scheduled');
        onClose();
      },
      onError: err => notify.error(err, "Couldn't cancel subscription"),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='Cancel subscription?'
      size='sm'
    >
      <div className='space-y-5 p-6'>
        {/* Access window - the single most reassuring fact, surfaced first. */}
        <div className='flex items-start gap-3 rounded-lg border border-border bg-muted p-4'>
          <CalendarClock className='mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground' />
          <div className='text-sm text-muted-foreground'>
            You'll keep full access until{' '}
            <span className='font-semibold text-foreground'>
              {accessEndsOn}
            </span>
            . No further charges - your plan simply drops to Free after that.
          </div>
        </div>

        {/* What stays vs what pauses, so the trade-off is concrete. */}
        <div className='space-y-2.5'>
          <div className='flex items-start gap-2.5 text-sm text-muted-foreground'>
            <Check className='mt-0.5 h-4 w-4 flex-shrink-0 text-success' />
            <span>
              Your lessons, mindspace, and progress stay available on Free.
            </span>
          </div>
          <div className='flex items-start gap-2.5 text-sm text-muted-foreground'>
            <PauseCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-warning' />
            <span>
              New AI generations pause once your monthly free budget runs out.
            </span>
          </div>
        </div>

        <p className='text-xs text-muted-foreground'>
          Changed your mind? You can resume anytime before {accessEndsOn}.
        </p>

        <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
          <Button
            data-testid='confirm-cancel-subscription'
            variant='ghost'
            size='sm'
            onClick={handleConfirm}
            disabled={cancel.isPending}
            className='text-muted-foreground hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30 dark:hover:text-red-300 max-sm:h-11'
          >
            {cancel.isPending ? (
              <>
                <Loader2 className='mr-1 h-4 w-4 animate-spin' />
                Canceling…
              </>
            ) : (
              'Cancel anyway'
            )}
          </Button>
          <Button
            size='sm'
            className='max-sm:h-11'
            onClick={onClose}
            disabled={cancel.isPending}
          >
            Keep my subscription
          </Button>
        </div>
      </div>
    </Modal>
  );
}
